import React, { useState } from 'react';
import {
  FileSpreadsheet,
  DollarSign,
  Users,
  Building,
  Briefcase,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Calendar,
  PieChart,
  BarChart3,
} from 'lucide-react';
import PLBenchmarkComparison from './PLBenchmarkComparison';

// Types matching backend CanonicalPL schema
interface PLLineItem {
  original_label: string;
  amount: number;
  category: string;
  expense_category?: string;
  confidence: number;
  requires_review: boolean;
}

interface PLPeriod {
  period_start: string;
  period_end: string;
  period_type: string;
  period_label?: string;
  
  // Summary totals
  total_revenue: number;
  total_cogs: number;
  gross_profit: number;
  total_operating_expenses: number;
  operating_income: number;
  net_income: number;
  
  // Phase 1.5: Granular expense breakdown
  officer_compensation: number;
  salaried_wages: number;
  hourly_wages: number;
  total_payroll: number;
  rent: number;
  utilities: number;
  insurance: number;
  professional_services: number;
  depreciation: number;
  debt_service: number;
  taxes_licenses: number;
  other_expenses_granular: number;
  
  // Line items
  revenue_items: PLLineItem[];
  cogs_items: PLLineItem[];
  expense_items: PLLineItem[];
}

interface ParsedPLStatement {
  source_file: string;
  source_format: string;
  parser_used: string;
  parsed_at: string;
  business_name?: string;
  periods: PLPeriod[];
  annual_summary?: {
    total_revenue: number;
    total_cogs: number;
    gross_profit: number;
    total_operating_expenses: number;
    net_income: number;
    officer_compensation: number;
    salaried_wages: number;
    hourly_wages: number;
    total_payroll: number;
    rent: number;
    utilities: number;
    insurance: number;
    professional_services: number;
    depreciation: number;
    debt_service: number;
    taxes_licenses: number;
    other_expenses_granular: number;
    period_count: number;
  };
  extraction_confidence: number;
  completeness_score: number;
  ready_for_future_closure: boolean;
}

interface ParsedPLDisplayProps {
  data: ParsedPLStatement;
  showDetails?: boolean;
  reportId?: string;  // For benchmark comparison
  businessType?: string;  // For benchmark comparison
}

const formatCurrency = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${(value * 100).toFixed(0)}%`;
};

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Category colors for expense breakdown
const EXPENSE_COLORS: Record<string, string> = {
  officer_compensation: 'bg-purple-500',
  salaried_wages: 'bg-blue-500',
  hourly_wages: 'bg-cyan-500',
  rent: 'bg-amber-500',
  utilities: 'bg-yellow-500',
  insurance: 'bg-green-500',
  professional_services: 'bg-indigo-500',
  depreciation: 'bg-gray-500',
  debt_service: 'bg-red-500',
  taxes_licenses: 'bg-orange-500',
  other: 'bg-slate-400',
};

const EXPENSE_LABELS: Record<string, string> = {
  officer_compensation: 'Officer Compensation',
  salaried_wages: 'Salaried Wages',
  hourly_wages: 'Hourly Wages',
  rent: 'Rent',
  utilities: 'Utilities',
  insurance: 'Insurance',
  professional_services: 'Professional Services',
  depreciation: 'Depreciation',
  debt_service: 'Debt Service',
  taxes_licenses: 'Taxes & Licenses',
  other_expenses_granular: 'Other Expenses',
};

export default function ParsedPLDisplay({ data, showDetails = true, reportId, businessType }: ParsedPLDisplayProps) {
  const [expandedPeriods, setExpandedPeriods] = useState<Set<number>>(new Set());
  const [showLineItems, setShowLineItems] = useState(false);
  const [showBenchmark, setShowBenchmark] = useState(false);

  const togglePeriod = (index: number) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedPeriods(newExpanded);
  };

  const summary = data.annual_summary;
  const confidenceColor = data.extraction_confidence >= 0.8 ? 'text-green-600' : 
                          data.extraction_confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600';

  // Calculate expense breakdown for visualization
  const getExpenseBreakdown = (period: PLPeriod | typeof summary) => {
    if (!period) return [];
    const expenses = [
      { key: 'officer_compensation', value: period.officer_compensation || 0 },
      { key: 'salaried_wages', value: period.salaried_wages || 0 },
      { key: 'hourly_wages', value: period.hourly_wages || 0 },
      { key: 'rent', value: period.rent || 0 },
      { key: 'utilities', value: period.utilities || 0 },
      { key: 'insurance', value: period.insurance || 0 },
      { key: 'professional_services', value: period.professional_services || 0 },
      { key: 'depreciation', value: period.depreciation || 0 },
      { key: 'debt_service', value: period.debt_service || 0 },
      { key: 'taxes_licenses', value: period.taxes_licenses || 0 },
      { key: 'other_expenses_granular', value: period.other_expenses_granular || 0 },
    ].filter(e => e.value > 0);
    
    const total = expenses.reduce((sum, e) => sum + e.value, 0);
    return expenses.map(e => ({ ...e, percent: total > 0 ? (e.value / total) * 100 : 0 }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {data.business_name || 'Parsed P&L Statement'}
              </h3>
              <p className="text-sm text-gray-500">
                {data.periods.length} period{data.periods.length !== 1 ? 's' : ''} • 
                Parsed {formatDate(data.parsed_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {data.ready_for_future_closure ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Ready for Analysis
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                Needs Review
              </span>
            )}
            <div className="text-right">
              <div className={`text-sm font-semibold ${confidenceColor}`}>
                {formatPercent(data.extraction_confidence)} Confidence
              </div>
              <div className="text-xs text-gray-500">
                via {data.parser_used.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Annual Summary */}
      {summary && (
        <div className="px-6 py-4 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Annual Summary ({summary.period_count} periods)
          </h4>
          
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-xs text-green-600 font-medium mb-1">Total Revenue</div>
              <div className="text-xl font-bold text-green-700">{formatCurrency(summary.total_revenue)}</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="text-xs text-amber-600 font-medium mb-1">Cost of Goods</div>
              <div className="text-xl font-bold text-amber-700">{formatCurrency(summary.total_cogs)}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-xs text-blue-600 font-medium mb-1">Operating Expenses</div>
              <div className="text-xl font-bold text-blue-700">{formatCurrency(summary.total_operating_expenses)}</div>
            </div>
            <div className={`p-3 rounded-lg ${summary.net_income >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <div className={`text-xs font-medium mb-1 ${summary.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                Net Income
              </div>
              <div className={`text-xl font-bold ${summary.net_income >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {formatCurrency(summary.net_income)}
              </div>
            </div>
          </div>

          {/* Payroll Breakdown (Phase 1.5) */}
          <div className="mb-6">
            <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Payroll Breakdown (3-Way Split)
            </h5>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="text-xs text-purple-600 font-medium">Officer Compensation</div>
                <div className="text-lg font-bold text-purple-700">{formatCurrency(summary.officer_compensation)}</div>
                <div className="text-xs text-purple-500">
                  {summary.total_payroll > 0 ? `${((summary.officer_compensation / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="text-xs text-blue-600 font-medium">Salaried Wages</div>
                <div className="text-lg font-bold text-blue-700">{formatCurrency(summary.salaried_wages)}</div>
                <div className="text-xs text-blue-500">
                  {summary.total_payroll > 0 ? `${((summary.salaried_wages / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
              <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                <div className="text-xs text-cyan-600 font-medium">Hourly Wages</div>
                <div className="text-lg font-bold text-cyan-700">{formatCurrency(summary.hourly_wages)}</div>
                <div className="text-xs text-cyan-500">
                  {summary.total_payroll > 0 ? `${((summary.hourly_wages / summary.total_payroll) * 100).toFixed(0)}% of payroll` : '-'}
                </div>
              </div>
            </div>
            <div className="mt-2 text-right text-sm text-gray-600">
              Total Payroll: <span className="font-semibold">{formatCurrency(summary.total_payroll)}</span>
            </div>
          </div>

          {/* Expense Breakdown Visual */}
          <div>
            <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              Expense Categories
            </h5>
            <div className="space-y-2">
              {getExpenseBreakdown(summary).map(({ key, value, percent }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-32 text-xs text-gray-600 truncate">
                    {EXPENSE_LABELS[key] || key}
                  </div>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${EXPENSE_COLORS[key] || 'bg-gray-400'} rounded-full transition-all`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="w-24 text-right text-sm font-medium text-gray-700">
                    {formatCurrency(value)}
                  </div>
                  <div className="w-12 text-right text-xs text-gray-500">
                    {percent.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Period Details */}
      {showDetails && data.periods.length > 0 && (
        <div className="px-6 py-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Period Details
          </h4>
          <div className="space-y-2">
            {data.periods.map((period, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Period Header */}
                <button
                  onClick={() => togglePeriod(index)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedPeriods.has(index) ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900">
                      {period.period_label || `${period.period_start} - ${period.period_end}`}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-200 rounded text-gray-600">
                      {period.period_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-600 font-medium">
                      Revenue: {formatCurrency(period.total_revenue)}
                    </span>
                    <span className={period.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                      Net: {formatCurrency(period.net_income)}
                    </span>
                  </div>
                </button>

                {/* Period Expanded Content */}
                {expandedPeriods.has(index) && (
                  <div className="px-4 py-4 bg-white border-t border-gray-200">
                    {/* Period Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">Revenue</div>
                        <div className="font-semibold text-green-600">{formatCurrency(period.total_revenue)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">COGS</div>
                        <div className="font-semibold text-amber-600">{formatCurrency(period.total_cogs)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">Gross Profit</div>
                        <div className="font-semibold text-blue-600">{formatCurrency(period.gross_profit)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">Net Income</div>
                        <div className={`font-semibold ${period.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(period.net_income)}
                        </div>
                      </div>
                    </div>

                    {/* Payroll Split */}
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <div className="text-xs font-medium text-blue-700 mb-2">Payroll Split</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Officer:</span>{' '}
                          <span className="font-medium">{formatCurrency(period.officer_compensation)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Salaried:</span>{' '}
                          <span className="font-medium">{formatCurrency(period.salaried_wages)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Hourly:</span>{' '}
                          <span className="font-medium">{formatCurrency(period.hourly_wages)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Fixed Costs */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-2 bg-amber-50 rounded text-center">
                        <div className="text-xs text-amber-600">Rent</div>
                        <div className="font-medium text-amber-700">{formatCurrency(period.rent)}</div>
                      </div>
                      <div className="p-2 bg-yellow-50 rounded text-center">
                        <div className="text-xs text-yellow-600">Utilities</div>
                        <div className="font-medium text-yellow-700">{formatCurrency(period.utilities)}</div>
                      </div>
                      <div className="p-2 bg-green-50 rounded text-center">
                        <div className="text-xs text-green-600">Insurance</div>
                        <div className="font-medium text-green-700">{formatCurrency(period.insurance)}</div>
                      </div>
                    </div>

                    {/* Line Items Toggle */}
                    {(period.revenue_items?.length > 0 || period.expense_items?.length > 0) && (
                      <button
                        onClick={() => setShowLineItems(!showLineItems)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {showLineItems ? 'Hide' : 'Show'} Line Items
                      </button>
                    )}

                    {/* Line Items Tables */}
                    {showLineItems && (
                      <div className="mt-4 space-y-4">
                        {/* Revenue Items */}
                        {period.revenue_items?.length > 0 && (
                          <div>
                            <h6 className="text-xs font-semibold text-gray-600 mb-2">Revenue Items</h6>
                            <table className="w-full text-sm">
                              <tbody>
                                {period.revenue_items.map((item, i) => (
                                  <tr key={i} className="border-b border-gray-100">
                                    <td className="py-1.5 text-gray-700">{item.original_label}</td>
                                    <td className="py-1.5 text-right font-medium text-green-600">
                                      {formatCurrency(item.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Expense Items */}
                        {period.expense_items?.length > 0 && (
                          <div>
                            <h6 className="text-xs font-semibold text-gray-600 mb-2">Expense Items</h6>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-1.5 text-gray-500 font-medium">Item</th>
                                  <th className="text-left py-1.5 text-gray-500 font-medium">Category</th>
                                  <th className="text-right py-1.5 text-gray-500 font-medium">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {period.expense_items.map((item, i) => (
                                  <tr key={i} className="border-b border-gray-100">
                                    <td className="py-1.5 text-gray-700">{item.original_label}</td>
                                    <td className="py-1.5">
                                      <span className={`px-2 py-0.5 text-xs rounded ${EXPENSE_COLORS[item.expense_category || ''] ? 'bg-opacity-20 ' + EXPENSE_COLORS[item.expense_category || ''].replace('bg-', 'bg-') : 'bg-gray-100'} text-gray-700`}>
                                        {EXPENSE_LABELS[item.expense_category || ''] || item.expense_category || item.category}
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-right font-medium text-gray-900">
                                      {formatCurrency(item.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmark Comparison Section */}
      {reportId && showDetails && (
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => setShowBenchmark(!showBenchmark)}
            className="w-full flex items-center justify-between py-2"
          >
            <div className="flex items-center gap-2 text-gray-700">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Industry Benchmark Comparison</span>
            </div>
            <span className="text-sm text-blue-600">{showBenchmark ? 'Hide' : 'Show'}</span>
          </button>
          
          {showBenchmark && (
            <div className="mt-4">
              <PLBenchmarkComparison reportId={reportId} businessType={businessType} />
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
        <span>
          Source: {data.source_file} ({data.source_format.toUpperCase()})
        </span>
        <span>
          Completeness: {formatPercent(data.completeness_score)}
        </span>
      </div>
    </div>
  );
}
